import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import TrialBanner from "../components/TrialBanner";
import { api, errMsg, formatCurrency, formatDate } from "../lib/api";
import { Plus, FolderKanban, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import ProjectSetupWizard from "../components/wizard/ProjectSetupWizard";
import { analytics } from "@heycatch/sdk";

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, setUser } = useAuth();
  const [showNew, setShowNew] = useState(!!user?.onboarding_pending);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [p, pr] = await Promise.all([api.get("/projects"), api.get("/programs")]);
      setProjects(p.data); setPrograms(pr.data);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const finishOnboarding = async () => {
    if (!user?.onboarding_pending) return;
    try { const { data } = await api.put("/account/preferences", { onboarding_pending: false }); setUser(data); }
    catch (e) { console.warn("Failed to clear onboarding flag", e); }
  };
  const closeWizard = () => { setShowNew(false); finishOnboarding(); };
  const onCreated = async (proj, meta = {}) => {
    const wasOnboarding = !!user?.onboarding_pending;
    analytics.trackEvent("project_created", { from_template: !!meta.fromTemplate, first_project: projects.length === 0 });
    if (projects.length === 0) analytics.trackEvent("first_project_created");
    if (wasOnboarding) analytics.trackEvent("onboarding_completed");
    await finishOnboarding();
    nav(`/app/projects/${proj.id}`);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try { await api.delete(`/projects/${id}`); setProjects((x)=>x.filter(p=>p.id!==id)); toast.success("Deleted"); } catch(e){ toast.error(errMsg(e)); }
  };

  return (
    <AppLayout>
      <TrialBanner />
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white" style={{fontFamily:'Outfit'}}>Projects</h1>
          <p className="text-slate-400 mt-1">Build plans, track actuals, measure variance.</p>
        </div>
        <button data-testid="new-project-btn" onClick={()=>setShowNew(true)} className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2.5 rounded-lg font-medium shadow-lg shadow-blue-900/30 hover:from-blue-400 hover:to-blue-600">
          <Plus size={16}/> New project
        </button>
      </div>

      <ProjectSetupWizard open={showNew} onClose={closeWizard} onCreated={onCreated}
        title={user?.onboarding_pending ? "Welcome to Compass PM — let's set up your first project" : "New project setup"} />

      {loading ? (
        <div className="text-slate-400">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="pc-card p-10 text-center" data-testid="empty-projects">
          <FolderKanban className="mx-auto text-blue-400 mb-3" size={36}/>
          <div className="text-xl font-semibold text-white mb-1" style={{fontFamily:'Outfit'}}>No projects yet</div>
          <p className="text-slate-400 mb-4">Create your first plan or start from a template.</p>
          <div className="flex gap-2 justify-center">
            <button onClick={()=>setShowNew(true)} className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg" data-testid="empty-create-project">Create a project</button>
            <Link to="/app/templates" className="border border-slate-700 text-slate-200 hover:border-slate-500 px-4 py-2 rounded-lg">Browse templates</Link>
          </div>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="projects-grid">
          {projects.map((p)=> {
            const program = programs.find((pr)=>pr.id === p.program_id);
            return (
              <div key={p.id} className="pc-card p-5 flex flex-col gap-2 group" data-testid={`project-card-${p.id}`}>
                <Link to={`/app/projects/${p.id}`} className="flex-1">
                  <div className="text-lg font-semibold text-white group-hover:text-blue-300" style={{fontFamily:'Outfit'}}>{p.name}</div>
                  <p className="text-sm text-slate-400 line-clamp-2 min-h-[2.2rem]">{p.description || "No description"}</p>
                  <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
                    {p.stated_due_date && <span>Due {formatDate(p.stated_due_date)}</span>}
                    {p.stated_budget ? <span>Budget {formatCurrency(p.stated_budget)}</span> : null}
                    {program && <span className="px-2 py-0.5 bg-blue-500/15 border border-blue-500/30 rounded-full text-blue-300">{program.name}</span>}
                  </div>
                </Link>
                <div className="flex items-center justify-between pt-2 border-t border-slate-800/60">
                  <Link to={`/app/projects/${p.id}`} className="text-xs text-blue-400 hover:text-blue-300" data-testid={`open-project-${p.id}`}>Open →</Link>
                  <button onClick={()=>remove(p.id)} data-testid={`delete-project-${p.id}`} className="text-slate-500 hover:text-rose-400"><Trash2 size={14}/></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}
